/**
 * Canonical number-formatting policy — the SINGLE source of truth for how
 * numeric values are displayed everywhere in Smart Insights (KPI cards,
 * charts, tooltips, tables, Performance Lens, Value-at-Stake).
 *
 * Backend always sends RAW numbers; the UI owns all formatting. Display
 * values are rounded for readability, but the actual full-precision value
 * is always available on hover via `formatActual`.
 *
 * Policy (confirmed with client):
 *   - >= $1B            ->  $X.XXB        (2 dp, abbreviated)
 *   - >= $1M            ->  $X.XXM        (2 dp, abbreviated)
 *   - >= $1K            ->  $X.XXK        (2 dp, abbreviated)
 *   - <  $1K            ->  $XXX.XX       (full number, 2 dp). e.g. $83.61, -$5.00
 *   - percent           ->  X.XX%         (2 dp)
 *   - KPI currency rates below $0.01 -> 4 dp when nonzero (e.g. $0.0024/CS)
 *
 * Non-USD plain numbers follow the same magnitude rules without the `$`.
 */

export interface FormatOptions {
  /** A unit symbol ("$", "%"), unit name ("USD", "pct"), or printf-ish
   *  format hint. Matched leniently. */
  unit?: string | null;
  /** Optional printf-style fixed-decimal display format, such as `,.4f`. */
  format?: string | null;
}

export interface KpiFormatOptions extends FormatOptions {
  /** True only for currency divided by a physical/business unit, such as
   * USD/CS, USD/KG, or USD/LB. */
  currencyPerUnit?: boolean;
}

export interface CurrencyPerUnitKpiDescriptor {
  unit?: string | null;
  label?: string | null;
  sourceField?: string | null;
}

type UnitKind = 'usd' | 'pct' | 'other' | 'none';

function classifyUnit(unit?: string | null): { kind: UnitKind; suffix: string } {
  const u = (unit ?? '').trim();
  if (!u) return { kind: 'none', suffix: '' };
  const lower = u.toLowerCase();
  if (lower.includes('pct') || lower.includes('percent') || u === '%') {
    return { kind: 'pct', suffix: '' };
  }
  if (lower.includes('usd') || u.includes('$')) {
    return { kind: 'usd', suffix: '' };
  }
  // Callers sometimes pass a printf-style format spec (".0f", "%.2f",
  // ",.2f", "d", "0.00") where a unit is expected. These are NOT units —
  // render a plain number rather than appending the spec to the value.
  if (/^[%,]?\.?\d*[dfeg%]?$/i.test(u)) {
    return { kind: 'none', suffix: '' };
  }
  // Genuine unit labels are short alphabetic tokens (CS, lb, pal, cases).
  if (/^[a-z]{1,6}$/i.test(u)) {
    return { kind: 'other', suffix: u };
  }
  return { kind: 'none', suffix: '' };
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const n = Number(value.replace(/,/g, '').trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Identify a currency-per-unit KPI from semantic metadata, never its value. */
export function isCurrencyPerUnitKpi({
  unit,
  label,
  sourceField,
}: CurrencyPerUnitKpiDescriptor): boolean {
  const normalizedUnit = (unit ?? '').trim();
  const isCurrency = /(?:^|[^a-z])usd(?:[^a-z]|$)/i.test(normalizedUnit)
    || normalizedUnit.includes('$');
  if (!isCurrency) return false;

  const unitDeclaresRate = /(?:\/|\bper\b)\s*[a-z0-9]+/i.test(normalizedUnit);
  const sourceDeclaresRate = /(?:^|_)per_[a-z0-9]+(?:_|$)/i.test(sourceField ?? '');
  const labelDeclaresRate = /(?:\bper\b|\/[a-z0-9]+\b)/i.test(label ?? '');
  return unitDeclaresRate || sourceDeclaresRate || labelDeclaresRate;
}

function fixedFractionDigits(format?: string | null): number | null {
  const match = (format ?? '').trim().match(/^[%,]?\.(\d+)f$/i);
  if (!match) return null;
  const digits = Number(match[1]);
  return Number.isInteger(digits) && digits >= 0 && digits <= 20 ? digits : null;
}

/**
 * Display value per the canonical policy. Abbreviates at Millions and up
 * (2 dp); shows sub-million values in full with comma grouping.
 */
export function formatValue(value: unknown, opts: FormatOptions = {}): string {
  const num = toNumber(value);
  if (num === null) return typeof value === 'string' ? value : '--';

  const { kind, suffix } = classifyUnit(opts.unit);
  if (kind === 'pct') return `${num.toFixed(2)}%`;

  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num);
  const prefix = kind === 'usd' ? '$' : '';

  let body: string;
  if (abs >= 1_000_000_000) {
    body = `${(abs / 1_000_000_000).toFixed(2)}B`;
  } else if (abs >= 1_000_000) {
    body = `${(abs / 1_000_000).toFixed(2)}M`;
  } else if (abs >= 1_000) {
    body = `${(abs / 1_000).toFixed(2)}K`;
  } else {
    // Sub-thousand: full number with 2 dp (e.g. 83.61, 950.00, 0.00).
    body = abs.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  const tail = kind === 'other' && suffix ? ` ${suffix}` : '';
  return `${sign}${prefix}${body}${tail}`;
}

/** KPI-only formatting for currency-per-unit rates.
 *
 * Normal currency values keep the canonical two-decimal policy. A nonzero
 * rate below one cent uses four decimals so a real value is not presented as
 * zero. Callers outside KPI cards should continue using `formatValue`.
 */
export function formatKpiValue(
  value: unknown,
  opts: KpiFormatOptions = {},
): string {
  const num = toNumber(value);
  const { kind } = classifyUnit(opts.unit);
  if (
    num === null
    || !opts.currencyPerUnit
    || kind !== 'usd'
    || num === 0
    || Math.abs(num) >= 0.01
  ) {
    return formatValue(value, opts);
  }

  const sign = num < 0 ? '-' : '';
  const body = Math.abs(num).toLocaleString(undefined, {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
  return `${sign}$${body}`;
}

/**
 * Full-precision value for hover/tooltips — never abbreviated. The user can
 * always see the actual underlying number behind a rounded display value.
 */
export function formatActual(value: unknown, opts: FormatOptions = {}): string {
  const num = toNumber(value);
  if (num === null) return typeof value === 'string' ? value : '--';

  const { kind, suffix } = classifyUnit(opts.unit);
  const explicitDigits = fixedFractionDigits(opts.format);
  if (kind === 'pct') {
    return `${num.toLocaleString(undefined, {
      ...(explicitDigits === null ? {} : { minimumFractionDigits: explicitDigits }),
      maximumFractionDigits: explicitDigits ?? 2,
    })}%`;
  }

  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num);
  const prefix = kind === 'usd' ? '$' : '';
  const body = abs.toLocaleString(undefined, {
    minimumFractionDigits: explicitDigits ?? (kind === 'usd' ? 2 : 0),
    maximumFractionDigits: explicitDigits ?? 2,
  });
  const tail = kind === 'other' && suffix ? ` ${suffix}` : '';
  return `${sign}${prefix}${body}${tail}`;
}
